import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';

import { useFullStory } from '@goshippo/utilities';

import { throttle } from 'lodash';
import { useSelector } from 'react-redux';

import { getUser } from '~/selectors/user.selectors';
import { getCookie } from '~/utils/cookies';
import { useFullStoryBillingPlan } from '~/utils/fullstory/hooks/useFullStoryUserVars/useFullStoryBillingPlan';
import { useFullStoryBillingStatus } from '~/utils/fullstory/hooks/useFullStoryUserVars/useFullStoryBillingStatus';

import { useFullStoryFeatureFlags } from './useFullStoryFeatureFlags';
import { useFullStoryStoreConnections } from './useFullStoryStoreConnections';

type UserVariables = Record<string, unknown>;
export type SetUserVariables = Dispatch<SetStateAction<UserVariables>>;

/**
 * Calls to `setUserVars` must be throttled to avoid sending too many requests to FullStory.
 *
 * FullStory has limits on how many requests can be sent in a given time frame.
 *
 * For more details, read:
 *
 * https://developer.fullstory.com/browser/identification/set-user-properties/#limits
 */
const THROTTLE_TIME = 5000;

/**
 * Forwards user vars to FullStory
 */
export const useFullStoryUserVars = () => {
  const { isInitialized, setUserVars } = useFullStory();

  const isFullStoryInitialized = isInitialized();
  const user = useSelector(getUser);
  const isImpersonatingCookie = getCookie('is_impersonate') || '';
  const isImpersonating = isImpersonatingCookie.toLowerCase() === 'true';

  // Variables that are pending to be sent to FullStory
  const pendingVariables = useRef({});
  const flushPendingVariables = useRef(
    throttle(() => {
      setUserVars({ ...pendingVariables.current });
      pendingVariables.current = {};
    }, THROTTLE_TIME),
  );
  const setUserVariables: SetUserVariables = useCallback(
    (argument) => {
      if (!isFullStoryInitialized) return;
      if (!user) return;
      if (isImpersonating) return;

      if (argument instanceof Function) {
        pendingVariables.current = argument(pendingVariables.current);
      } else {
        pendingVariables.current = {
          ...pendingVariables.current,
          ...argument,
        };
      }

      flushPendingVariables.current();
    },
    [isFullStoryInitialized, isImpersonating, user],
  );

  useEffect(() => {
    if (!user) pendingVariables.current = {};
  }, [user]);

  useFullStoryFeatureFlags({ setUserVariables });
  useFullStoryStoreConnections({ setUserVariables });
  useFullStoryBillingPlan({ setUserVariables });
  useFullStoryBillingStatus({ setUserVariables });
};
